import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import api from "../../Services/api";
import "./style.css";

// Tag ko API ke format me badlo: lowercase, "#" se shuru, sirf a-z 0-9 _
// (API baaki tags chupchaap hata deti hai, isliye yahin sahi kar dete hain)
export const normalizeHashtag = (text = "") => {
  const tag = String(text)
    .trim()
    .toLowerCase()
    .replace(/^#+/, "")
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  if (!tag || /^\d+$/.test(tag)) return "";
  return `#${tag}`;
};

const toOption = (tag) => ({ value: tag, label: tag });

// Admin ke baaki inputs jaisa look
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    background: "var(--bg)",
    borderRadius: 5,
    border: `solid 1px ${state.isFocused ? "var(--PurpleColor)" : "#f5f5f5"}`,
    boxShadow: "none",
    cursor: "text",
    "&:hover": { borderColor: state.isFocused ? "var(--PurpleColor)" : "#dbdbdb" },
  }),
  valueContainer: (base) => ({ ...base, padding: "4px 10px", gap: 6 }),
  placeholder: (base) => ({ ...base, color: "#999", fontSize: 14 }),
  input: (base) => ({ ...base, color: "var(--BlackDark)", fontSize: 14, margin: 0 }),
  multiValue: (base) => ({
    ...base,
    margin: 0,
    background: "var(--bluelight100)",
    borderRadius: 20,
    alignItems: "center",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--BlackDark)",
    fontSize: 13,
    padding: "3px 4px 3px 10px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--BlackDark)",
    borderRadius: "50%",
    width: 20,
    height: 20,
    marginRight: 4,
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": { background: "var(--PurpleColor)", color: "var(--WhiteColor)" },
  }),
  indicatorSeparator: (base) => ({ ...base, background: "#dbdbdb" }),
  dropdownIndicator: (base) => ({ ...base, color: "#999", "&:hover": { color: "var(--PurpleColor)" } }),
  clearIndicator: (base) => ({ ...base, color: "#999", cursor: "pointer", "&:hover": { color: "var(--RedColor)" } }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
    marginTop: 4,
    borderRadius: 8,
    overflow: "hidden",
    border: "solid 1px #dbdbdb",
    boxShadow: "0 8px 24px rgba(17, 28, 44, 0.12)",
  }),
  menuList: (base) => ({ ...base, maxHeight: 240, padding: 4 }),
  option: (base, state) => {
    // Type karte waqt wahi option highlight jo Enter dabane par judega
    const enterTag = state.selectProps.enterTag;
    const active = enterTag
      ? state.data.__isNew__ || state.data.value === enterTag
      : state.isFocused;
    return {
      ...base,
      fontSize: 14,
      borderRadius: 5,
      cursor: "pointer",
      color: "var(--BlackDark)",
      background: active ? "var(--bluelight100)" : "transparent",
      "&:hover": { background: "var(--bluelight100)" },
      "&:active": { background: "var(--bluelight300)" },
    };
  },
  noOptionsMessage: (base) => ({ ...base, fontSize: 14, color: "#999" }),
  loadingMessage: (base) => ({ ...base, fontSize: 14, color: "#999" }),
};

// Blog hashtags: list se chuno ya naya type karke Enter dabao.
// value / onChange: space se alag string ("#tally #vat"), API isi format me leti hai.
// required: kam se kam ek tag ke bina form submit na ho
export default function HashtagSelect({
  value = "",
  onChange,
  inputId = "Hashtag",
  required = false,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .post("/getAllHashtags")
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        const tags = [...new Set(list.map((t) => normalizeHashtag(t?.Hashtags)).filter(Boolean))];
        if (alive) setOptions(tags.map(toOption));
      })
      .catch((error) => console.error("Error fetching hashtags:", error))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const [input, setInput] = useState("");
  // User ne arrow key se list me koi option chuna hai? Tab Enter usi ko chune.
  const [navigated, setNavigated] = useState(false);

  const selected = value.split(/\s+/).filter(Boolean).map(toOption);

  const emit = (tags) => onChange([...new Set(tags)].join(" "));

  // Jo naam type kiya wahi tag bane ("#" khud lagta hai), list ka milta-julta tag nahi
  const addTyped = () => {
    const tag = normalizeHashtag(input);
    if (!tag) return false;
    emit([...selected.map((o) => o.value), tag]);
    setInput("");
    return true;
  };

  // Type kiya naam list me pehle se ho to wo sabse upar
  const typed = normalizeHashtag(input);
  const sortedOptions = typed
    ? [...options].sort((a, b) => (b.value === typed) - (a.value === typed))
    : options;

  return (
    <div className="hashtag-field">
      <CreatableSelect
        isMulti
        inputId={inputId}
        required={required}
        classNamePrefix="hashtag-select"
        options={sortedOptions}
        value={selected}
        isLoading={loading}
        closeMenuOnSelect={false}
        placeholder="Select or type hashtags"
        inputValue={input}
        onInputChange={(text, { action }) => {
          if (action === "input-change") setNavigated(false);
          if (action !== "input-blur" && action !== "menu-close") setInput(text);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") setNavigated(true);
          const create = (e.key === "Enter" && !navigated) || e.key === ",";
          if (create && input.trim()) {
            e.preventDefault();
            addTyped();
          }
        }}
        // Enter dabaye bina bahar click / Submit kiya to bhi tag ban jaye
        onBlur={() => addTyped() || setInput("")}
        createOptionPosition="first"
        enterTag={navigated ? "" : typed}
        onChange={(list) => emit((list || []).map((o) => o.value))}
        onCreateOption={(text) => {
          const tag = normalizeHashtag(text);
          if (tag) emit([...selected.map((o) => o.value), tag]);
          setInput("");
        }}
        isValidNewOption={(text, current, all) => {
          const tag = normalizeHashtag(text);
          return !!tag && !all.some((o) => o.value === tag) && !current.some((o) => o.value === tag);
        }}
        formatCreateLabel={(text) => `Add "${normalizeHashtag(text)}"`}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "Use letters, numbers or _ (not only numbers)" : "No hashtags found"
        }
        styles={selectStyles}
      />
      <small className="hashtag-help">
        Type any name and press Enter (# is added automatically), or pick from the list.
      </small>
    </div>
  );
}
